import { auth } from "@clerk/tanstack-react-start/server";
import { redirect } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";

import { authorizeRequestFromSearch } from "./connect-partner";
import { clearUser, setContext, setTag } from "./glitchtip";

export type AuthRedirectSearch = {
  /** Where in this app to land after sign-in; only a path on this origin is honoured. */
  redirectTo?: string;
  /**
   * Clerk's own parameter: the OAuth authorize request that sent the visitor here (Connect
   * Bavimail, an MCP client). Honoured only when it belongs to our Clerk instance.
   */
  redirect_url?: string;
  /** `manual` shows the full sign-in form even when a partner's authorize request is present. */
  connect?: "manual";
};

export const DEFAULT_POST_AUTH_REDIRECT = "/dashboard";

export function validateAuthRedirectSearch(search: Record<string, unknown>): AuthRedirectSearch {
  return {
    redirectTo: typeof search.redirectTo === "string" ? search.redirectTo : undefined,
    redirect_url: typeof search.redirect_url === "string" ? search.redirect_url : undefined,
    connect: search.connect === "manual" ? "manual" : undefined,
  };
}

/**
 * Where a signed-in visitor of the sign-in page goes: back to a trusted OAuth authorize
 * request when one brought them here (Clerk then shows its consent screen), else the
 * in-app path they asked for.
 */
export function getPostAuthHref(search?: AuthRedirectSearch): string {
  const authorize = authorizeRequestFromSearch(search?.redirect_url);
  return authorize?.href ?? getSafeRedirectPath(search?.redirectTo);
}

export function getSafeRedirectPath(redirectTo?: string, fallback = DEFAULT_POST_AUTH_REDIRECT) {
  if (!redirectTo) {
    return fallback;
  }

  try {
    const url = new URL(redirectTo, "http://bavimail.local");
    if (url.origin !== "http://bavimail.local") {
      return fallback;
    }

    if (!url.pathname.startsWith("/")) {
      return fallback;
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return fallback;
  }
}

export function buildLoginRouteHref(redirectTo?: string) {
  const safeRedirectPath = getSafeRedirectPath(redirectTo);
  return `/login?redirectTo=${encodeURIComponent(safeRedirectPath)}`;
}

const getClerkRouteAuth = createServerFn({ method: "GET" }).handler(async () => {
  const authState = await auth();
  return {
    isAuthenticated: authState.isAuthenticated,
    userId: authState.userId,
    sessionId: authState.sessionId,
  };
});

export async function redirectIfAuthenticated(search?: AuthRedirectSearch) {
  const authState = await getClerkRouteAuth();
  if (!authState.isAuthenticated) {
    return;
  }

  throw redirect({
    href: getPostAuthHref(search),
  });
}

export async function requireAuthenticatedRoute(currentHref: string) {
  const authState = await getClerkRouteAuth();

  if (authState.isAuthenticated && authState.userId) {
    await setTag({ key: "auth.provider", value: "clerk" });
    await setContext({
      name: "auth",
      context: {
        provider: "clerk",
        userId: authState.userId,
        sessionId: authState.sessionId,
      },
    });
    return {
      auth: authState,
    };
  }

  await clearUser();
  throw redirect({
    href: buildLoginRouteHref(currentHref),
  });
}
